import React from 'react';
import { css } from 'emotion';
import Link from 'gatsby-link';
import Row from './Row';
import fancyCurves from '../assets/img/fancy-curves-divider-down.svg';
import DownloadAppButton from './appComponents/DownloadAppButton';
import SectionSix from './SectionSix';
import styled from 'styled-components';

/* global tw */
const styles = {
    footer: css`
        ${tw`w-screen relative h-auto py-16 lg:py-20`};
        background: #0A0A0F;
        font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
        ::before{
            content: "";
            display: block;
            position: absolute;
            top: -9.85rem;
            height: 10rem;
            width: 100%;
            background-image: url(${fancyCurves});
            background-size: cover;
            background-repeat: no-repeat;
            transform: rotate(180deg);
            opacity: 0.25;
            filter: hue-rotate(280deg) saturate(1.8);
        }
    `,
    pText: css`
        ${tw`text-left lg:text-left font-normal`};
        color: #9898A8;
        font-size: 0.9375rem;
        line-height: 1.65;
        margin-top: 0.25rem;
        margin-bottom: 1rem;
    `,
   hText: css`
    ${tw`lg:w-1/2 w-full font-bold`};
    color: #F5F5F7;
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
    letter-spacing: -0.02em;
    margin-top: 0.25rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`,

    seoText: css`
        ${tw`w-full appearance-none no-underline`};
        color: #0A0A0F;
        font-size: 0.01rem;
    `,
}

const SmallFooterText = styled.div`
    ${tw`text-sm`};
    color: #5A5A6E;
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
`

const SmallFooterText1 = styled.div`
    ${tw`text-left lg:text-left tracking-tight`};
    color: #9898A8;
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
`

const JourneySection = styled.div`
    ${tw`w-full mb-16 lg:mb-20`};
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
`

const JourneyHeading = styled.div`
    ${tw`w-full mb-10 lg:mb-14`};
    p.eyebrow {
        font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.14em;
        font-size: 0.6875rem;
        color: #C9A2FF;
        margin-bottom: 0.875rem;
    }
    h2 {
        font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
        font-weight: 800;
        color: #F5F5F7;
        letter-spacing: -0.035em;
        line-height: 1.1;
        font-size: 2rem;
        margin: 0;
        @media (min-width: 992px) { font-size: 2.75rem; }
    }
    &::after {
        content: '';
        display: block;
        margin-top: 0.875rem;
        height: 2px;
        width: 2.5rem;
        background: #C9A2FF;
        border-radius: 2px;
    }
`

const JourneyGrid = styled.div`
    ${tw`w-full`};
    display: grid;
    gap: 1.25rem;
    grid-template-columns: 1fr;
    @media (min-width: 992px) {
        grid-template-columns: repeat(3, 1fr);
        gap: 1.5rem;
    }
`

const JourneyCard = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 2rem 1.75rem 1.75rem;
    background: linear-gradient(180deg, #13101A 0%, #0F0C15 100%);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 10px;
    overflow: hidden;
    transition: all 0.3s ease;
    @media (min-width: 992px) {
        padding: 2.5rem 2.25rem 2rem;
    }
    &::before {
        content: '';
        position: absolute;
        top: 0; left: 0;
        width: 100%; height: 1px;
        background: linear-gradient(90deg, transparent, rgba(201,162,255,0.45), transparent);
        opacity: 0.6;
        transition: opacity 0.3s ease;
    }
    &:hover {
        border-color: rgba(201, 162, 255, 0.35);
        transform: translateY(-3px);
        box-shadow: 0 24px 48px rgba(0, 0, 0, 0.45);
    }
    &:hover::before {
        opacity: 1;
    }
`

const CardIndex = styled.div`
    ${tw`flex items-baseline mb-6`};
    gap: 0.75rem;
    .num {
        font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
        font-weight: 800;
        font-size: 1.625rem;
        background: linear-gradient(135deg, #D8B4FE 0%, #C9A2FF 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        letter-spacing: -0.03em;
        line-height: 1;
    }
    .rule {
        flex: 1;
        height: 1px;
        background: linear-gradient(90deg, rgba(201, 162, 255, 0.35), transparent);
    }
`

const CardTitle = styled.h3`
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
    font-weight: 800;
    font-size: 1.375rem;
    color: #F5F5F7;
    letter-spacing: -0.03em;
    line-height: 1.2;
    margin: 0 0 1rem 0;
    @media (min-width: 992px) { font-size: 1.5rem; }
`

const CardBody = styled.p`
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
    font-weight: 400;
    font-size: 0.9875rem;
    color: #9898A8;
    line-height: 1.7;
    letter-spacing: -0.003em;
    margin: 0 0 1.75rem 0;
    flex: 1;
    @media (min-width: 992px) { font-size: 1rem; }
`

const CardMeta = styled.p`
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.6875rem;
    letter-spacing: 0.14em;
    color: #7A6A3A;
    margin: 0 0 1.5rem 0;
    padding-top: 1.25rem;
    border-top: 1px solid rgba(201, 162, 255, 0.12);
`

const CardCTA = styled(Link)`
    display: inline-flex;
    align-items: center;
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
    font-weight: 700;
    font-size: 0.9375rem;
    color: #F5F5F7;
    text-decoration: none;
    letter-spacing: -0.005em;
    transition: color 0.25s ease;
    .arrow {
        margin-left: 0.5rem;
        color: #C9A2FF;
        transition: transform 0.25s ease;
    }
    &:hover {
        color: #D8B4FE;
    }
    &:hover .arrow {
        transform: translateX(4px);
    }
`

const JOURNEYS = [
    {
        num: '01',
        title: 'For the boardroom.',
        body: `Your 8:40 pickup is waiting before you've tied your shoes. The chauffeur knows the route, the traffic, and when not to make small talk. You arrive reviewing the deck, not apologizing for being late.`,
        meta: 'Hourly · Airport · Corporate accounts',
        cta: 'Book a business ride',
        to: '/',
    },
    {
        num: '02',
        title: 'For the weekend.',
        body: `Four hours to Coorg shouldn't feel like four hours of driving. Pack the family, pick a car that fits the luggage and the kids, and let someone else handle the ghats. One chauffeur, the whole trip.`,
        meta: 'Outstation · Multi-day · Family SUVs',
        cta: 'Plan an outstation trip',
        to: '/',
    },
    {
        num: '03',
        title: 'For the milestone.',
        body: `A wedding procession. A 25th anniversary. Your parents' first ride in a car they've only seen in ads. Zyppys handles the vehicles, the chauffeurs, and the small touches — so the only thing on your mind that day is the day itself.`,
        meta: 'Weddings · Events · Luxury fleet',
        cta: 'Arrange a special occasion',
        to: '/',
    },
]

const keywords = [
    { from: "Hyderabad", to: "Bangalore" },
    { from: "hyderabad", to: "Yadagirigutta" },
    { from: "hyderabad", to: "srisailam" },
    { from: "hyderabad", to: "Karimnagar" },
    { from: "hyderabad", to: "vijayawada" },
    { from: "hyderabad", to: "warangal" },
    { from: "bangalore", to: "Chikmagalur" },
    { from: "bangalore", to: "mysore" },
    { from: "bangalore", to: "coorg" },
    { from: "bangalore", to: "ooty" },
    { from: "bangalore", to: "nandi hills" },
    { from: "bangalore", to: "pondicherry" },
    { from: "bangalore", to: "tirupati" },
    { from: "bangalore", to: "kodaikanal" },
    { from: "bangalore", to: "chennai" },
    { from: "bangalore", to: "wayanad" },
    { from: "delhi", to: "gurgaon" },
    { from: "delhi", to: "lansdowne" },
    { from: "delhi", to: "jaipur" },
    { from: "delhi", to: "rishikesh" },
    { from: "delhi", to: "agra" },
    { from: "delhi", to: "shimla" },
    { from: "delhi", to: "chandigarh" },
    { from: "delhi", to: "kasol" },
    { from: "delhi", to: "mathura" },
    { from: "mumbai", to: "pune" },
    { from: "mumbai", to: "alibaug" },
    { from: "mumbai", to: "nashik" },
]

const selfDriveKeywords = [
    "hyderabad", "bangalore", "chennai", "mysore", "secunderabad"
]

const Footer = (props) => (
    <footer className={styles.footer}>
        {/* "Built for every kind of journey" block hidden per request
        <Row justifyBetween>
            <JourneySection>
                <JourneyHeading>
                    <p className="eyebrow">Where Zyppys takes you</p>
                    <h2>Built for every kind of journey.</h2>
                </JourneyHeading>
                <JourneyGrid>
                    {JOURNEYS.map(card => (
                        <JourneyCard key={card.num}>
                            <CardIndex>
                                <span className="num">{card.num}</span>
                                <span className="rule" />
                            </CardIndex>
                            <CardTitle>{card.title}</CardTitle>
                            <CardBody>{card.body}</CardBody>
                            <CardMeta>{card.meta}</CardMeta>
                            <CardCTA to={card.to}>
                                {card.cta}
                                <span className="arrow">→</span>
                            </CardCTA>
                        </JourneyCard>
                    ))}
                </JourneyGrid>
            </JourneySection>
            <h4 className={css`${tw`text-white lg:w-1/2 w-full font-normal tracking-wide`}`}>
                Download app from play store or app store to enjoy greater benefits.
            </h4>
        </Row>
        */}

        <SectionSix />

        <Row justifyBetween>

            <div className={css`${tw`w-full lg:w-1/2 mt-4`}`}>
                <img className={css`${tw``}; height: 3rem;`}
                    src={require('../assets/img/logo-with-light-text.svg')}
                    alt="Zyppys Logo" />
                <p className={css`
                    ${tw`leading-normal mt-4`};
                    color: #9898A8;
                    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
                    font-size: 0.9375rem;
                    line-height: 1.65;
                `}>
                    T-Hub<br />
                    Gachibowli, Hyderabad<br />
                    Telangana - 500081
                </p>
            </div>
            <div className={css`${tw`w-full lg:text-right lg:w-1/2`}`}>
                <p className={css`
                    text-transform: uppercase;
                    letter-spacing: 0.12em;
                    font-size: 0.75rem;
                    font-weight: 700;
                    color: #CB53D1;
                    margin-bottom: 0.5rem;
                `}>Any Questions?</p>
                <a href="tel:+918688901902" className={css`
                    ${tw`text-left lg:text-right no-underline`};
                    display: inline-flex;
                    align-items: center;
                    padding: 0.625rem 1.125rem;
                    border-radius: 6px;
                    background: rgba(255, 255, 255, 0.04);
                    border: 1px solid rgba(255, 255, 255, 0.10);
                    color: #F5F5F7 !important;
                    font-weight: 700;
                    font-size: 0.9375rem;
                    letter-spacing: -0.005em;
                    margin-bottom: 1rem;
                    transition: all 0.2s ease;
                    &:hover {
                        background: rgba(255, 255, 255, 0.07);
                        border-color: rgba(201, 162, 255, 0.4);
                        transform: translateY(-1px);
                        box-shadow: 0 0 20px rgba(0, 0, 0, 0.35);
                    }
                `}>Call Us : +91 8688 901 902</a><br />
                <Link to="/aboutus/" className={css`${tw`text-left lg:text-right font-medium tracking-tight`}; color: #9898A8; transition: color 0.2s ease; &:hover { color: #CB53D1; }; text-decoration:none`}>About Us</Link><br />
                <Link to="/partners/" className={css`${tw`text-left lg:text-right font-medium tracking-tight`}; color: #9898A8; transition: color 0.2s ease; &:hover { color: #CB53D1; }; text-decoration:none`}>Partner With Us</Link>


            </div>
        </Row>
        <Row justifyBetween className={css`${tw`py-8 mt-8`}; border-top: 1px solid rgba(255, 255, 255, 0.06); border-bottom: 1px solid rgba(255, 255, 255, 0.06);`}>
            <h3 className={css`${tw`lg:w-1/2 w-full font-normal tracking-wide`}; color: #9898A8;`}></h3>
            <DownloadAppButton className={css`${tw`opacity-80`}`} />
        </Row>
        <Row justifyBetween className={css`${tw`mt-6`}`}>
            <SmallFooterText>An entity of Twilight IT Professionals Private Limited <br />
                CIN: U72200TG2014PTC093906
            </SmallFooterText>
            <div className={css`${tw`lg:text-right`}`}>
                <SmallFooterText><Link to="/terms/" className={css`
                    ${tw`no-underline`};
                    color: #5A5A6E;
                    transition: color 0.2s ease;
                    &:hover { color: #CB53D1; }
                `}>Terms &amp; Conditions</Link></SmallFooterText>
                <SmallFooterText>&copy; Copyrights Reserved.</SmallFooterText>
            </div>
        </Row>
        <Row>
            <div className={styles.seoText}>
                <p>Contactless Travel – The New Normal - Post-COVID -19</p>
                <h1>Rental Car &  Self Drive Cars Rental in India</h1>
                <p>If traveling is a lot more than a hobby and a little less than addiction, then Zyppys is the app for you! Why should I book only through Zyppys?</p>
                <p>It is India's first specialized car rental marketplace. Zyppys App is a one-stop-shop for all your <h2 className={css`${tw`inline-block`}`}><a href="https://zyppys.com">rental car, car rentals</a></h2>, <h2 className={css`${tw`inline-block`}`}><a href="https://zyppys.com/self-drive-cars">self-drive cars </a></h2>,  <h4 className={css`${tw`inline-block`}`}><a href="https://zyppys.com/outstation-cabs">outstation cab</a></h4>, cab rental, home to office transportation, <h3 className={css`${tw`inline-block`}`}><a href="https://zyppys.com/self-drive-car-rental-in-hyderabad">rental car in India,</a></h3> self drive car rental in India, safe rental cars after lockdown, clean cars, private transportation after lockdown and daily transportation need post- <h5 className={css`${tw`inline-block`}`}><a href="https://www.who.int" target="_blank">COVID -19 </a></h5>. It makes it easy for you to browse, compare, and rent your favorite car instantly at the best price from top car rental companies in India (savaari, ahataxis, and more). You can choose rental cars that come with the driver, self-drive, from and to the airport, Car for Outstation, or select point to point daily transportation needs. You can choose from different cars, different pricing to suit your budget and we will ensure you have a safe contactless journey protecting you and your family.</p>
            </div>
        </Row>
    </footer>
);

export default Footer;
